import WiggotLogo from '@/assets/svg/wiggot-logo.svg'
import { AmplitudeEvents } from '@/interfaces/enum'
import { IProperty } from '@monopolio-ui/types-library'
import { useAmplitude } from '@wiggot/analytics/src/providers/amplitud-provider/AmplitudeProvider'
import { GoogleIcon, useModalAddListStore } from '@wiggot/web-components'
import useMatchWindowQuery from '@wiggot/web-components/hooks/ds/useMatchWindowQuery'
import Transition from '@wiggui/ds/Transition'
import Image from 'next/image'
import { IconDetail } from './CardComponents'

interface IDetail {
  icon: string
  label: string | number
  active: boolean
}

interface IWiggotMetadata {
  id: string
  commissionRate?: number
}

interface IPropertyCompose extends IProperty {
  wiggotMetadata?: IWiggotMetadata
  isWiggotProperty?: boolean
}

interface IProps {
  property?: IPropertyCompose
  idHover?: string
  mapboxGLIsSupported?: boolean
  isShowTour?: boolean
  onFlyToMap?: (propertyId: string) => void
  onShowMap?: () => void
}

const OPERATION_TYPES = {
  'for-sale': { label: 'VENTA', color: 'bg-notif-green' },
  'for-rent': { label: 'RENTA', color: 'bg-notif-turquoise' }
}

const PROPERTY_TYPE_DESCRIPTION: Record<string, string> = {
  house: 'Casa',
  apartment: 'Departamento',
  apartament: 'Departamento',
  terrain: 'Terreno',
  land: 'Terreno',
  commercial: 'Comercial',
  shop: 'Local',
  office: 'Oficina',
  industrial: 'Industrial',
  storage: 'Bodega',
  warehouse: 'Bodega',
  other: 'Otro',
  shop_shopping_center: 'Local en centro comercial',
  consulting_room: 'Consultorio',
  industrial_building: 'Nave industrial',
  ranch: 'Rancho/Hacienda',
  building: 'Edificio',
  hotel: 'Hotel',
  event_hall: 'Salón/Terraza',
  cottage: 'Cabaña',
  country_house: 'Quinta Campestre'
}

export const CardHeader = ({
  property,
  idHover,
  isShowTour,
  mapboxGLIsSupported,
  onFlyToMap,
  onShowMap
}: IProps) => {
  const { trackEvent, isInitialized } = useAmplitude()
  const { isMD } = useMatchWindowQuery()

  const { isWiggotProperty = false, wiggotMetadata: { id = '' } = { id: '' } } =
    property || {}
  const setModalAddList = useModalAddListStore(
    (state) => state?.setModalAddList
  )

  return (
    <div className="p-4 w-full h-full flex justify-end items-start">
      <Transition show={idHover === property?.id || !isMD || isShowTour}>
        <div className="flex gap-2 tour-search-step-2-3">
          {mapboxGLIsSupported && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                onFlyToMap?.(property ? property.id : id)
                onShowMap?.()
              }}
              className="flex p-2 justify-center items-center rounded-full bg-surface-white"
            >
              <GoogleIcon
                name="my_location"
                className="text-primary font-semibold"
              />
            </button>
          )}
          {((isWiggotProperty && id) || isShowTour) && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                setModalAddList({
                  isOpen: true,
                  propertyId: id?.split('_')[0],
                  origin: 'SEARCH'
                })
                if (isInitialized) {
                  trackEvent({
                    name: AmplitudeEvents.SEARCH_RESULTS_ADDPROPERTYLIST_VIEW
                  })
                }
              }}
              className="flex p-2 justify-center items-center rounded-full bg-surface-white"
            >
              <GoogleIcon
                name="playlist_add"
                className="text-primary font-semibold"
              />
            </button>
          )}
        </div>
      </Transition>
    </div>
  )
}

const surfaceTerrainTypes = ['terrain', 'ranch', 'event_hall']

export const CardFooter = ({ property }: Partial<IProps>) => {
  const {
    propertyType = '',
    fullAddress,
    numBedrooms = '',
    numParkingLots = '',
    numBathrooms = '',
    terrainSurface = '',
    price,
    listingType = '',
    isWiggotProperty,
    wiggotMetadata = {},
    surfaceTotal = ''
  } = property || {}
  const operation = OPERATION_TYPES[listingType as keyof typeof OPERATION_TYPES]

  const surface = surfaceTerrainTypes?.includes(property?.propertyType || '')
    ? terrainSurface
    : surfaceTotal
  const propertyDetail: IDetail[] = [
    {
      icon: 'bed',
      label: numBedrooms?.toString(),
      active: Boolean(numBedrooms)
    },
    {
      icon: 'bathtub',
      label: numBathrooms?.toString(),
      active: Boolean(numBathrooms)
    },
    {
      icon: 'directions_car',
      label: `${numParkingLots} Est.`,
      active: Boolean(numParkingLots)
    },
    {
      icon: 'pageless',
      label: `${surface?.toLocaleString()} m²`,
      active: Boolean(surface)
    }
  ].filter((item) => item?.active)
  const commission = (commission: string, type: string) => {
    if (type === 'months') {
      if (commission === '1') {
        return `${commission || '-'} mes`
      }
      return `${commission || '-'} meses`
    }
    return `${commission || '- '}%`
  }
  return (
    <div className="flex flex-col h-[202px] px-[16px] pb-[16px] gap-2 justify-end">
      {isWiggotProperty && (
        <div className="h-[32px] w-[32px] rounded-full flex justify-center items-center border-2 border-white overflow-hidden">
          <Image src={WiggotLogo} alt="wiggot image" width={32} height={32} />
        </div>
      )}
      <div className="flex gap-2 w-fit">
        <div className="text-dark rounded-full px-2 py-1 w-max text-[10px] font-bold bg-white">
          {PROPERTY_TYPE_DESCRIPTION[propertyType]}
        </div>
        <div
          className={`text-white rounded-full px-2 py-1 w-max text-[10px] font-bold ${operation?.color}`}
        >
          {listingType ? operation?.label : '-'}
        </div>
      </div>
      <div className="w-full flex gap-2 justify-between">
        {listingType === 'for-sale' && (
          <div className="p-2 bg-notif-green-01 backdrop-blur-[7px] rounded-lg w-[48%] h-[50px] text-white">
            <div className="h-full flex flex-col justify-center pl-1">
              <div className="text-white flex items-end">
                <span className="text-base font-semibold">
                  ${price?.toLocaleString()}
                </span>
                <span className="text-[10px] mb-[3px]">MXN</span>
              </div>
              {isWiggotProperty &&
                Boolean(
                  (wiggotMetadata as IWiggotMetadata)?.commissionRate
                ) && (
                  <span className="text-xs text-notif-green">
                    Comisión:{' '}
                    {commission(
                      `${(wiggotMetadata as IWiggotMetadata)?.commissionRate}`,
                      'percentage'
                    )}
                  </span>
                )}
            </div>
          </div>
        )}
        {listingType === 'for-rent' && (
          <div className="p-2 bg-[#00c0c433] backdrop-blur-[7px] rounded-lg w-[48%] h-[50px] text-white">
            <div className="h-full flex flex-col justify-center pl-1">
              <div className="text-white flex items-end">
                <span className="text-base font-semibold">
                  ${price?.toLocaleString()}
                </span>
                <span className="text-[10px] mb-[3px]">MXN</span>
              </div>
              {isWiggotProperty &&
                Boolean(
                  (wiggotMetadata as IWiggotMetadata)?.commissionRate
                ) && (
                  <span className="text-xs text-notif-turquoise">
                    Comisión:{' '}
                    {commission(
                      `${(wiggotMetadata as IWiggotMetadata)?.commissionRate}`,
                      'months'
                    )}
                  </span>
                )}
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col backdrop-blur-[7px] bg-[#ffffff1a] p-2 rounded-lg">
        <p className="text-surface-gray-20 text-sm text-ellipsis overflow-hidden w-full truncate ...">
          {fullAddress}
        </p>
        <div className="flex items-center gap-[6px] text-surface-gray-20  w-full">
          {propertyDetail?.map((item, index) => (
            <IconDetail
              key={`${item?.icon}-${Math.random() * index * Math.random()}`}
              icon={item?.icon}
              label={item?.label}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
